import ReactMarkdown from "react-markdown";
import { useLocation } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { TFLocationState } from "types/topicflow";

import {
  canOpenInSidebar,
  getInternalPathname,
} from "@apps/main/routes-constants";
import AppLink from "@components/link/link";
import { toWithBackground } from "@helpers/helpers";

// The AI sometimes generates markdown links with a space between ] and (
// e.g. [Title] (https://...) instead of [Title](https://...).
// Standard markdown parsers don't recognize that as a link.
const normalizeMarkdownLinks = (md: string): string =>
  md.replace(/(\[[^\]]+\])\s+(\([^)]+\))/g, "$1$2");

export const AskAIMessageMarkdown = ({ children }: { children: string }) => {
  const location = useLocation<TFLocationState>();
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children, ...props }) => (
          <p className="text-base font-bold my-2" {...props}>
            {children}
          </p>
        ),
        h2: ({ children, ...props }) => (
          <p className="text-[15px] font-bold my-2" {...props}>
            {children}
          </p>
        ),
        h3: ({ children, ...props }) => (
          <p className="text-sm font-bold my-1.5" {...props}>
            {children}
          </p>
        ),
        h4: ({ children, ...props }) => (
          <p className="text-sm font-bold my-1.5" {...props}>
            {children}
          </p>
        ),
        h5: ({ children, ...props }) => (
          <p className="text-sm font-bold my-1.5" {...props}>
            {children}
          </p>
        ),
        h6: ({ children, ...props }) => (
          <p className="text-sm font-bold my-1.5" {...props}>
            {children}
          </p>
        ),
        a: ({ href, children, ...props }) => {
          if (!href) {
            return null;
          }
          let url: URL | null = null;
          try {
            url = new URL(href, window.location.origin);
          } catch {
            return <span {...props}>{children}</span>;
          }
          const allowed = new Set(["http:", "https:", "mailto:", "tel:"]);
          if (!allowed.has(url.protocol)) {
            return <span {...props}>{children}</span>;
          }
          const internalPathname = getInternalPathname(href);
          const isSidebarUrl = canOpenInSidebar(href);

          // Check if it's an internal link
          if (internalPathname) {
            return (
              <AppLink
                to={
                  isSidebarUrl
                    ? toWithBackground({
                        pathname: internalPathname,
                        location,
                      })
                    : internalPathname
                }
                {...props}
              >
                {children}
              </AppLink>
            );
          }
          // Fallback to external link
          return (
            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
              {children}
            </a>
          );
        },
      }}
    >
      {normalizeMarkdownLinks(children)}
    </ReactMarkdown>
  );
};

export default AskAIMessageMarkdown;
